/**
 * 预约数据管理 Composable
 * 管理预约数据的获取、更新等功能
 */

import axios from 'axios';
import config from '@/config';
import * as sleepDataUtils from '@/utils/sleepDataUtils';
import { parseApiResponse, handleApiError, snakeToCamel } from '@/utils/apiResponse';

export default function useAppointmentData() {
  return {
    data() {
      return {
        appointmentData: [],
        todayAppointmentData: [], // 存储当日预约记录数据
        isProcessingAppointmentData: false // 数据操作锁，防止竞态条件
      };
    },

    methods: {
      /**
       * 获取预约数据
       */
      async fetchAppointmentData() {
        // 防止并发请求
        if (this.isProcessingAppointmentData) {
          return;
        }

        this.isProcessingAppointmentData = true;

        try {
          const storeId = this.effectiveStoreId;
          if (!storeId) {
            this.appointmentData = [];
            return;
          }

          const apiUrl = config.getApiUrl(
            config.apiEndpoints.appointments.list,
            storeId
          );

          const response = await axios.get(apiUrl);
          
          // 使用统一的响应解析函数处理 ApiResponse 格式
          const result = parseApiResponse(response);
          
          if (result.success && result.data && Array.isArray(result.data) && result.data.length > 0) {
            // 将下划线字段名转换为驼峰命名
            const camelData = result.data.map(item => snakeToCamel(item));
            
            // 验证并修正预约状态
            const correctedData = this.validateAndCorrectAppointmentStatus(camelData);
            
            // 合并现有数据（保留实时数据、waveData等）
            const mergedData = this.mergeAppointmentData(this.appointmentData, correctedData);
            
            // 标记新预约
            const markedData = this.markNewAppointments(mergedData);
            
            this.appointmentData = markedData;
            
            if (this.addSystemLog) {
              this.addSystemLog('成功获取预约数据');
            }
          } else {
            this.appointmentData = [];
            if (this.addSystemLog) {
              this.addSystemLog('API返回数据处理后为空');
            }
          }

          this.$nextTick(() => {
            if (this.updateRotation) {
              this.updateRotation();
            }
          });
        } catch (error) {
          this.appointmentData = [];
          if (this.addSystemLog) {
            this.addSystemLog('处理预约数据异常: ' + (error.message || '未知错误'));
          }
        } finally {
          this.isProcessingAppointmentData = false;
        }
      },

      /**
       * 获取当日预约记录
       */
      async fetchTodayAppointmentData() {
        try {
          const storeId = this.effectiveStoreId;
          if (!storeId) {
            this.todayAppointmentData = [];
            return;
          }

          const apiUrl = config.getApiUrl(
            config.apiEndpoints.appointments.today,
            storeId
          );

          const response = await axios.get(apiUrl);
          
          // 使用统一的响应解析函数处理 ApiResponse 格式
          const result = parseApiResponse(response);
          
          if (result.success && result.data && Array.isArray(result.data)) {
            // 将下划线字段名转换为驼峰命名
            this.todayAppointmentData = result.data.map(item => snakeToCamel(item));
          } else {
            this.todayAppointmentData = [];
          }
        } catch (error) {
          this.todayAppointmentData = [];
        }
      },

      /**
       * 检查即将开始的预约
       */
      checkUpcomingAppointments() {
        const now = new Date();
        let hasNewAppointment = false;

        this.appointmentData.forEach((appointment) => {
          if (!appointment || !appointment.startTime || !appointment.endTime) {
            return;
          }

          const startTime = sleepDataUtils.parseTimeString(appointment.startTime);
          const endTime = sleepDataUtils.parseTimeString(appointment.endTime);

          if (!startTime || !endTime) {
            return;
          }

          if (now >= startTime) {
            const appointmentId = `${appointment.roomName}-${appointment.patientName}-${appointment.startTime}`;

            if (!appointment.hasShownStartNotification) {
              if (this.addSystemLog) {
                this.addSystemLog(
                  `预约到点显示：${appointment.roomName} - ${appointment.patientName}`
                );
              }

              appointment.hasShownStartNotification = true;
              appointment.isNew = true;
              hasNewAppointment = true;
            }
          }
        });

        if (hasNewAppointment) {
          const tempData = [...this.appointmentData];
          this.appointmentData = tempData;
        }
      },

      /**
       * 验证并修正预约状态
       */
      validateAndCorrectAppointmentStatus(appointmentData) {
        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let correctedCount = 0;
        
        const correctedData = appointmentData.map((appointment) => {
          if (!appointment) {
            return appointment;
          }
          
          let appointmentDate = null;
          if (appointment.appointmentDate) {
            appointmentDate = new Date(appointment.appointmentDate);
            appointmentDate.setHours(0, 0, 0, 0);
          } else if (appointment.startTime) {
            const startTime = sleepDataUtils.parseTimeString(appointment.startTime);
            if (startTime) {
              appointmentDate = new Date(startTime);
              appointmentDate.setHours(0, 0, 0, 0);
            }
          }
          
          if (appointmentDate && appointmentDate < today) {
            if (appointment.treatmentStatus !== '已结束') {
              correctedCount++;
              if (this.addSystemLog) {
                this.addSystemLog('修正历史预约状态：' + appointment.roomName + ' - ' + appointment.patientName);
              }
            }
            
            return Object.assign({}, appointment, {
              treatmentStatus: '已结束',
              isHistorical: true
            });
          }
          
          return appointment;
        });
        
        if (correctedCount > 0 && this.addSystemLog) {
          this.addSystemLog('共修正了 ' + correctedCount + ' 个历史预约的治疗状态');
        }
        
        return correctedData;
      },

      /**
       * 合并预约数据（保留实时数据、waveData等）
       * 这是一个简化版本，完整版本在主文件中
       */
      mergeAppointmentData(existingData, newData) {
        if (!existingData || existingData.length === 0) {
          return newData || [];
        }

        if (!newData || newData.length === 0) {
          return existingData;
        }

        // 简化的合并逻辑：保留现有数据的实时数据、waveData等
        return newData.map(newItem => {
          if (!newItem || !newItem.roomId) {
            return newItem;
          }

          const existingItem = existingData.find(item => 
            item && item.roomId && 
            item.roomId.toString() === newItem.roomId.toString()
          );

          if (existingItem) {
            // 保留实时数据和waveData
            return {
              ...newItem,
              lastValidBrainwaveData: existingItem.lastValidBrainwaveData,
              lastReceiveTime: existingItem.lastReceiveTime,
              waveData: existingItem.waveData || newItem.waveData,
              realtimeDataArray: existingItem.realtimeDataArray,
              currentDisplayIndex: existingItem.currentDisplayIndex,
              bubbles: existingItem.bubbles || newItem.bubbles,
              lastBubbleTime: existingItem.lastBubbleTime
            };
          }

          return newItem;
        });
      },

      /**
       * 标记新预约
       */
      markNewAppointments(newData) {
        if (this.appointmentData.length === 0) {
          return newData;
        }

        let hasNewAppointment = false;
        newData.forEach((appointment) => {
          const existingIndex = this.appointmentData.findIndex(
            (item) =>
              item.roomName === appointment.roomName &&
              item.patientName === appointment.patientName &&
              item.startTime === appointment.startTime &&
              item.endTime === appointment.endTime
          );

          if (existingIndex === -1) {
            appointment.isNew = true;
            hasNewAppointment = true;

            if (this.addSystemLog) {
              this.addSystemLog(
                `发现新预约：${appointment.roomName} - ${appointment.patientName}`
              );
            }

            setTimeout(() => {
              const index = this.appointmentData.findIndex(
                (item) =>
                  item.roomName === appointment.roomName &&
                  item.patientName === appointment.patientName &&
                  item.startTime === appointment.startTime &&
                  item.endTime === appointment.endTime
              );

              if (index !== -1) {
                this.appointmentData[index].isNew = false;
              }
            }, 3000);
          } else {
            appointment.hasShownStartNotification =
              this.appointmentData[existingIndex].hasShownStartNotification;
          }
        });

        if (hasNewAppointment && this.updateRotation) {
          this.updateRotation();
        }

        return newData;
      }
    }
  };
}

